import React, { useState, useMemo } from "react";
import "./Journey.css";

// JSONs com traduções
import prod_actvs from "../../pages/v2/jsons/prod_actvs.json";
import countries from "../../pages/v2/jsons/countries.json";
import compositions from "../../pages/v2/jsons/compositions.json";

import { useTranslation } from "react-i18next";

const Journey = ({ steps = [], onCompanyClick }) => {
  const { t, i18n } = useTranslation();
  const [expandedStep, setExpandedStep] = useState(null);
  const [expandedFiber, setExpandedFiber] = useState(null);

  const lang = (i18n.language?.split("-")[0] || "en").toLowerCase();

  // ---- helpers genéricos para JSON { description: { en, pt, ... } }
  const getLocalizedDescription = (item, fallbackKey) => {
    if (!item || !item.description) return t(fallbackKey);
    return item.description[lang] || item.description.en || t(fallbackKey);
  };

  // ---- reverse (sem mutar)
  const reversedSteps = useMemo(() => {
    if (!Array.isArray(steps) || steps.length === 0) return [];
    if (steps[0]?.production_activity_code !== "PA_FIBER") return [...steps].reverse();
    return steps;
  }, [steps]);

  // ---- traduções
  const getAPDescription = (code) => {
    const ap = prod_actvs.find((x) => x.code === code);
    return getLocalizedDescription(ap, "unknownActivity");
  };

  const getCountryDescription = (code) => {
    const country = countries.find((x) => x.code === code);
    return getLocalizedDescription(country, "unknown-country");
  };

  const getFiberDescription = (code) => {
    const fiber = compositions.find((x) => x.code === code);
    return getLocalizedDescription(fiber, "undefined");
  };

  const codeMapping = {
    PA_FIBER: { img: "/images/icon_raw_material_v2_journey.svg" },
    PA_SPINNING: { img: "/images/icon_spinning_v2_journey.svg" },
    PA_KNITTING: { img: "/images/icon_knitting_v2_journey.svg" },
    PA_FINISHING: { img: "/images/icon_finishing_new.svg" },
    PA_DYEING: { img: "/images/icon_dyeing_and_finishing_v2_journey.svg" },
    PA_WEAVING: { img: "/images/icon_weaving_v2_journey.svg" },
    PA_ULTIMATION: { img: "/images/icon_ultimation_journey.svg" },
    PA_MANUFACTURING: { img: "/images/icon_sewing_v2_journey.svg" },
    PA_CLIENT: { img: "/images/icon_client_v2_journey.svg" },
  };

  const getTitleAndImage = (code) => codeMapping[code] || { img: "icon_unknown.svg" };

  const toggleExpand = (index) => setExpandedStep((prev) => (prev === index ? null : index));
  const toggleFiberExpand = (fiberIndex) =>
    setExpandedFiber((prev) => (prev === fiberIndex ? null : fiberIndex));

  return (
    <div className="journey-map-container">
      <div className="vertical-line"></div>

      <div className="journey-map">
        {reversedSteps.map((step, index) => {
          const { img } = getTitleAndImage(step.production_activity_code);
          const isExpanded = expandedStep === index;

          return (
            <React.Fragment key={`${step.production_activity_code}-${index}`}>
              <div className="journey-map-step">
                <img src={img} alt="icon-step" className="icon-step" />

                <div className="step-show-details">
                  <button
                    type="button"
                    aria-expanded={isExpanded}
                    aria-controls={`journey-step-${index}`}
                    aria-label={`${isExpanded ? t("collapse") : t("expand")} ${getAPDescription(step.production_activity_code)}`}
                    className="journey-map-step-title-container"
                    onClick={() => toggleExpand(index)}
                  >
                    <div
                      className={`journey-map-step-title ${isExpanded ? "expanded" : ""}`}
                    >
                      {getAPDescription(step.production_activity_code)}
                    </div>

                    <div
                      className={`journey-map-step-plus ${isExpanded ? "expanded" : ""}`}
                    >
                      <span className="plus-sign">+</span>
                    </div>
                  </button>

                  <div
                    id={`journey-step-${index}`}
                    role="region"
                    aria-label={getAPDescription(step.production_activity_code)}
                    hidden={!isExpanded}
                    className={`step-details ${isExpanded ? "expanded" : ""}`}
                  >
                    {step.production_activity_code === "PA_FIBER" ? (
                      <div className="fibers-container">
                        {(step.companies || []).map((fiber, fiberIndex) => (
                          <div
                            key={`${fiber.fiber}-${fiberIndex}`}
                            className="fiber-item"
                          >
                            <div className="separator-journey-container">
                              <div className="separator-journey"></div>
                            </div>

                            <button
                              type="button"
                              aria-expanded={expandedFiber === fiberIndex}
                              aria-controls={`journey-fiber-${index}-${fiberIndex}`}
                              aria-label={`${expandedFiber === fiberIndex ? t("collapse") : t("expand")} ${getFiberDescription(fiber.fiber)}`}
                              className="fiber-title-container"
                              onClick={() => toggleFiberExpand(fiberIndex)}
                            >
                              <div className="fiber-title">
                                {getFiberDescription(fiber.fiber)}
                              </div>

                              <span
                                className={`journey-map-step-plus ${
                                  expandedFiber === fiberIndex ? "expanded" : ""
                                }`}
                              >
                                <span className="plus-sign">+</span>
                              </span>
                            </button>

                            <div
                              id={`journey-fiber-${index}-${fiberIndex}`}
                              role="region"
                              aria-label={getFiberDescription(fiber.fiber)}
                              hidden={expandedFiber !== fiberIndex}
                              className={`fiber-details ${expandedFiber === fiberIndex ? "expanded" : ""}`}
                            >
                              <div className="separator-journey-container">
                                <div className="separator-journey"></div>
                              </div>

                              <div className="journey-expanded-column">
                                <div className="step-text-company-container">
                                  <div className="step-text-company">
                                    {fiber.name
                                      ? fiber.name
                                      : t("company-not-provided")}
                                  </div>
                                  {fiber.name && onCompanyClick && (
                                    <button type="button" aria-label={`${t("companyCertificates.subtitle")}: ${fiber.name}`} className="company-certificates-icon" onClick={() => onCompanyClick(fiber)}>
                                      <img src="/images/icon_certificacoes.svg" alt="" />
                                    </button>
                                  )}
                                </div>

                                <div className="step-text-country">
                                  {fiber.country
                                    ? getCountryDescription(fiber.country)
                                    : t("country-not-provided")}
                                </div>
                                {Array.isArray(fiber.additionalActivities) &&
                                  fiber.additionalActivities.length > 0 &&
                                  fiber.additionalActivities.some(
                                    (obj) =>
                                      (obj?.activity ?? "").trim().length > 0,
                                  ) && (
                                    <div className="additional-activities-container">
                                      <div className="step-text-country additional-activity-title">
                                        {t("additional-activities")}
                                      </div>

                                      <div className="additional-activities-map">
                                        {fiber.additionalActivities.map(
                                          (object, aaIndex) => (
                                            <div
                                              key={`${object?.name || "aa"}-${aaIndex}`}
                                              className="journey-expanded-separator additional-activities"
                                            >
                                              <div className="separator-journey-container">
                                                <div className="separator-journey"></div>
                                              </div>

                                              <div className="journey-expanded-column">
                                                {(object?.activity ?? "").trim()
                                                  .length > 0 && (
                                                  <div className="step-text-country additional-activity">
                                                    {object.activity}
                                                  </div>
                                                )}

                                                <div className="step-text-company-container additional-activities">
                                                  <div className="step-text-company additional-activities">
                                                    {object?.name
                                                      ? object.name
                                                      : t(
                                                          "company-not-provided",
                                                        )}
                                                  </div>
                                                  {object?.name &&
                                                    onCompanyClick && (
                                                      <button type="button" aria-label={`${t("companyCertificates.subtitle")}: ${object.name}`} className="company-certificates-icon" onClick={() => onCompanyClick(object)}><img src="/images/icon_certificacoes.svg" alt="" /></button>
                                                    )}
                                                </div>

                                                <div className="step-text-country additional-activities">
                                                  {object?.country
                                                    ? getCountryDescription(
                                                        object.country,
                                                      )
                                                    : t("country-not-provided")}
                                                </div>
                                              </div>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                    </div>
                                  )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div>
                        {(step.companies || []).map((object, companyIndex) => (
                          <div
                            key={`${object?.name || "company"}-${companyIndex}`}
                            className="journey-expanded-separator"
                          >
                            <div className="separator-journey-container">
                              <div className="separator-journey"></div>
                            </div>

                            <div className="journey-expanded-column">
                              <div className="step-text-company-container">
                                <div className="step-text-company">
                                  {object?.name
                                    ? object.name
                                    : t("company-not-provided")}
                                </div>
                                {object?.name && onCompanyClick && (
                                  <button type="button" aria-label={`${t("companyCertificates.subtitle")}: ${object.name}`} className="company-certificates-icon" onClick={() => onCompanyClick(object)}><img src="/images/icon_certificacoes.svg" alt="" /></button>
                                )}
                              </div>

                              <div className="step-text-country">
                                {object?.country
                                  ? getCountryDescription(object.country)
                                  : t("country-not-provided")}
                              </div>
                            </div>
                          </div>
                        ))}

                        {Array.isArray(step.additionalActivities) &&
                          step.additionalActivities.length > 0 &&
                          step.additionalActivities.some(
                            (obj) => (obj?.activity ?? "").trim().length > 0,
                          ) && (
                            <div className="additional-activities-container">
                              <div className="step-text-country additional-activity-title">
                                {t("additional-activities")}
                              </div>

                              <div className="additional-activities-map">
                                {step.additionalActivities.map(
                                  (object, aaIndex) => (
                                    <div
                                      key={`${object?.name || "aa"}-${aaIndex}`}
                                      className="journey-expanded-separator additional-activities"
                                    >
                                      <div className="separator-journey-container">
                                        <div className="separator-journey"></div>
                                      </div>

                                      <div className="journey-expanded-column">
                                        {(object?.activity ?? "").trim()
                                          .length > 0 && (
                                          <div className="step-text-country additional-activity">
                                            {object.activity}
                                          </div>
                                        )}

                                        <div className="step-text-company-container additional-activities">
                                          <div className="step-text-company additional-activities">
                                            {object?.name
                                              ? object.name
                                              : t("company-not-provided")}
                                          </div>
                                          {object?.name && onCompanyClick && (
                                            <button type="button" aria-label={`${t("companyCertificates.subtitle")}: ${object.name}`} className="company-certificates-icon" onClick={() => onCompanyClick(object)}><img src="/images/icon_certificacoes.svg" alt="" /></button>
                                          )}
                                        </div>

                                        <div className="step-text-country additional-activities">
                                          {object?.country
                                            ? getCountryDescription(
                                                object.country,
                                              )
                                            : t("country-not-provided")}
                                        </div>
                                      </div>
                                    </div>
                                  ),
                                )}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default Journey;
